// Bottom-fixed action bar. Surface the counts; gate the Publish button on
// `pending == 0`. Publish flushes any pending debounced save before firing.

import { useMemo } from "react";
import { useReviewStore } from "./store";
import { usePublish, useSaveReview } from "./api";

interface Props {
  docId: string;
  onPublished?: (publishedAt: string) => void;
  onPreview?: () => void;
}

export function PublishBar({ docId, onPublished, onPreview }: Props) {
  const actions = useReviewStore((s) => s.actions);
  const counts = useMemo(() => {
    const c = { total: 0, pending: 0, accepted: 0, edited: 0, rejected: 0, added: 0 };
    for (const id of Object.keys(actions)) {
      c.total++;
      const a = actions[id]?.action;
      if (a === "pending") c.pending++;
      else if (a === "accepted") c.accepted++;
      else if (a === "edited") c.edited++;
      else if (a === "rejected") c.rejected++;
      else if (a === "added") c.added++;
    }
    return c;
  }, [actions]);
  const saving = useReviewStore((s) => s.saving);
  const dirty = useReviewStore((s) => s.dirty);
  const { flush } = useSaveReview(docId);
  const { publish, publishing, error } = usePublish(docId);

  const canPublish = counts.pending === 0 && !publishing;

  const onPublish = async () => {
    await flush();
    const res = await publish();
    onPublished?.(res.published_at);
  };

  return (
    <div className="maisight-review-publish-bar" role="contentinfo" aria-label="Publish bar">
      <div className="maisight-review-publish-counts">
        <span>{counts.total} terms total</span>
        <span data-testid="count-pending">· {counts.pending} pending</span>
        <span>· {counts.accepted} accepted</span>
        <span>· {counts.edited} edited</span>
        <span>· {counts.rejected} rejected</span>
        <span>· {counts.added} added</span>
        {dirty || saving ? (
          <span className="maisight-review-publish-saving" data-testid="saving-indicator">
            {saving ? "saving…" : "unsaved"}
          </span>
        ) : null}
      </div>
      <div className="maisight-review-publish-actions">
        {onPreview ? (
          <button
            type="button"
            className="maisight-review-btn maisight-review-btn--ghost"
            onClick={onPreview}
          >
            Preview
          </button>
        ) : null}
        <button
          type="button"
          className="maisight-review-btn maisight-review-btn--primary"
          onClick={onPublish}
          disabled={!canPublish}
          aria-disabled={!canPublish}
          aria-label="Publish"
          data-testid="publish-button"
        >
          {publishing ? "Publishing…" : "Publish"}
        </button>
        {error ? <span className="maisight-review-publish-error">{error.message}</span> : null}
      </div>
    </div>
  );
}
